import { ReducerCreators, type PayloadAction } from '@reduxjs/toolkit';

import type { SeriesState } from './types';
// import type { RootState } from '@/store/main';
import LoadSeriesListForChannel from '@/connection/series/load-series-list-for-channel';
import LoadSeriesFull from '@/connection/series/load-series-full-by-series-uuid';
import LoadSeriesFullByEventUuid from '@/connection/series/load-series-full-by-event-uuid';
import CreateSeries from '@/connection/series/create-series';
import AddEventToSeries from '@/connection/series/add-event-to-series';
import DeleteEventFromSeries from '@/connection/series/delete-event-from-series';
import type { ICreateSeries, ISeriesPrototype, ISeriesWithEvents, SeriesEvent } from '@/types/series.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { FetchError } from '@/connection/helpers';
import SetSeriesEvents from '@/connection/series/set-series-events';
import { withoutEvent } from './utils';
import type { SelectedBrandliveEvent } from '@/types/projects.types';
import { eventStart } from '@/utils/event-start';
import { eventEnd } from '@/utils/event-end';
import { isObject } from 'underscore';
import { initialState } from './initial';

export const reducers = (create: ReducerCreators<SeriesState>) => ({
  loadSeriesListForChannel: create.asyncThunk(
    async (
      {
        channel,
        page,
        limit,
        withEvents,
        search,
      }: {
        channel: number;
        page?: number;
        limit?: number;
        withEvents?: boolean;
        search?: string;
      },
      { rejectWithValue }
    ) => {
      try {
        return await LoadSeriesListForChannel({ channel, page, limit, withEvents, search });
      } catch (e) {
        if (e instanceof FetchError) {
          return rejectWithValue(e.body);
        }

        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        // append unique rows each load with pagination
        state.seriesList = action.payload.series;
        state.loadingSeriesList = false;
        state.seriesError = false;
        state.totalSeries = action.payload.total;
        state.seriesListInitialLoaded = true;
      },
      pending: (state) => {
        state.loadingSeriesList = true;
      },
      rejected: (state, action) => {
        state.loadingSeriesList = false;
        state.seriesError = true;
        console.error(action.error);
        toastTrigger('There was an error loading series. Please refresh the page.', {
          type: 'error',
          autoClose: 10000,
        });
      },
    }
  ),
  loadSeriesFull: create.asyncThunk(
    async ({ channel, seriesUuid }: { channel: number; seriesUuid: string }, { rejectWithValue }) => {
      try {
        return await LoadSeriesFull({ channel, seriesUuid });
      } catch (e) {
        if (e instanceof FetchError) {
          return rejectWithValue(e.body);
        }

        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.currentSeries = action.payload;
        state.loadingCurrentSeries = false;
        state.seriesError = false;
      },
      pending: (state) => {
        state.loadingCurrentSeries = true;
      },
      rejected: (state, action) => {
        state.loadingCurrentSeries = false;
        state.seriesError = true;
        console.error(action.error);
        toastTrigger('There was an error loading series details. Please try again.', {
          type: 'error',
          autoClose: 10000,
        });
      },
    }
  ),
  loadCurrentSeriesForProject: create.asyncThunk(
    async ({ channel, eventUuid }: { channel: number; eventUuid: string }, { rejectWithValue }) => {
      try {
        return await LoadSeriesFullByEventUuid({ channel, eventUuid });
      } catch (e) {
        if (e instanceof FetchError) {
          return rejectWithValue(e.body);
        }

        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.currentSeries = action.payload;
        state.loadingCurrentSeries = false;
        state.seriesError = false;
      },
      pending: (state) => {
        state.loadingCurrentSeries = true;
      },
      rejected: (state, action) => {
        state.loadingCurrentSeries = false;
        if (isObject(action.payload) && action.payload.error !== 'No associated series') {
          console.error(action);
          state.seriesError = true;
          toastTrigger('There was an error loading series by event. Please try again.', {
            type: 'error',
            autoClose: 10000,
          });
        }
      },
    }
  ),
  createSeries: create.asyncThunk(
    async (series: ICreateSeries, { rejectWithValue }) => {
      try {
        return await CreateSeries(series);
      } catch (e) {
        if (e instanceof FetchError) {
          return rejectWithValue(e.body);
        }

        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.seriesList) {
          state.seriesList = [...state.seriesList, action.payload];
        }
        state.seriesError = false;
      },
      rejected: (state, action) => {
        state.seriesError = true;
        console.error(action.error);
        toastTrigger('There was an error creating the series. Please try again.', {
          type: 'error',
          autoClose: 10000,
        });
      },
    }
  ),
  addEventToSeries: create.asyncThunk(
    async (
      {
        channel,
        seriesUuid,
        project,
      }: {
        channel: number;
        seriesUuid: string;
        project: SelectedBrandliveEvent;
      },
      { rejectWithValue }
    ) => {
      try {
        return await AddEventToSeries({ channel, seriesUuid, eventUuid: project.uuid });
      } catch (e) {
        if (e instanceof FetchError) {
          return rejectWithValue(e.body);
        }

        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.seriesError = false;
        const proj = action.meta.arg.project;
        const seriesProject = {
          uuid: proj?.uuid || '',
          title: proj?.sessions[0].title ?? { base: '', changed: '' },
          timestamp: proj ? eventStart(proj) : 0,
          end_timestamp: proj ? eventEnd(proj) : 0,
          event_image: proj?.event_image,
        };

        state.currentSeries = state.currentSeries
          ? {
              ...state.currentSeries,
              events: [...state.currentSeries.events, seriesProject],
            }
          : null;

        state.seriesList =
          state.seriesList?.map((ser) => {
            if (ser.events && ser.uuid === action.payload.series_uuid) {
              ser.events.push(seriesProject);
            }
            return ser;
          }) || null;
      },
      rejected: (state, action) => {
        state.seriesError = true;
        console.error(action.error);
        toastTrigger('There was an error adding the event to series. Please try again.', {
          type: 'error',
          autoClose: 10000,
        });
      },
    }
  ),
  setSeriesEvents: create.asyncThunk(
    async (
      {
        channel,
        seriesUuid,
        eventUuids,
      }: {
        channel: number;
        seriesUuid: string;
        eventUuids: string[];
        eventData?: SeriesEvent[];
      },
      { rejectWithValue }
    ) => {
      try {
        return await SetSeriesEvents({ channel, seriesUuid, eventUuids });
      } catch (e) {
        if (e instanceof FetchError) {
          return rejectWithValue(e.body);
        }

        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.seriesError = false;
        state.seriesList =
          state.seriesList?.map((ser) => {
            if (ser.uuid === action.meta.arg.seriesUuid && action.meta.arg.eventData) {
              ser.events = action.meta.arg.eventData;
            }
            return ser;
          }) || null;

        if (state.currentSeries?.uuid === action.meta.arg.seriesUuid && action.meta.arg.eventData) {
          state.currentSeries = {
            ...state.currentSeries,
            events: action.meta.arg.eventData,
          };
        }
      },
      rejected: (state, action) => {
        state.seriesError = true;
        console.error(action.error);
        toastTrigger('There was an error adding the event to series. Please try again.', {
          type: 'error',
          autoClose: 10000,
        });
      },
    }
  ),
  deleteEventFromSeries: create.asyncThunk(
    async (
      {
        channel,
        seriesUuid,
        eventUuid,
      }: {
        channel: number;
        seriesUuid: string;
        eventUuid: string;
      },
      { rejectWithValue }
    ) => {
      try {
        await DeleteEventFromSeries({ channel, seriesUuid, eventUuid });
        return { eventUuid, seriesUuid };
      } catch (e) {
        if (e instanceof FetchError) {
          return rejectWithValue(e.body);
        }

        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.currentSeries?.events) {
          state.currentSeries = withoutEvent(action.payload)(state.currentSeries) as ISeriesWithEvents;
        }

        state.seriesList = state.seriesList?.filter(withoutEvent(action.payload)) || null;
        state.seriesError = false;
      },
      rejected: (state, action) => {
        state.seriesError = true;
        console.error(action.error);
        toastTrigger('There was an error removing the event from series. Please try again.', {
          type: 'error',
          autoClose: 10000,
        });
      },
    }
  ),
  clearCurrentSeries: create.reducer((state) => {
    state.currentSeries = null;
  }),
  clearSeriesList: create.reducer((state) => {
    state.seriesList = null;
  }),
  appendSeriesSync: create.reducer((state, action: PayloadAction<ISeriesPrototype>) => {
    if (state.seriesList) {
      state.seriesList.push(action.payload);
    } else {
      state.seriesList = [action.payload];
    }
  }),
  openTopDrawer: create.reducer((state) => {
    state.topDrawerOpen = true;
  }),
  closeTopDrawer: create.reducer((state) => {
    state.topDrawerOpen = false;
  }),
  setCurrentSeriesSync: create.reducer((state, action: PayloadAction<SeriesState['currentSeries']>) => {
    state.currentSeries = action.payload;
  }),
  resetSeriesReducer: create.reducer(() => {
    return initialState;
  }),
});
